import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import Cta from "../common/header/Cta";
import { useCarConfigSelector } from "../../../redux/store";
import Search from "./Search";
import SpecGroup from "./SpecGroup";
import { selectCarConfigSpecsSearchIsSearching } from "../../../redux/slices/CarConfigSpecsSearchSlice";
import { useAphStepTracking } from "../../../hooks/useAphStepTracking";
import Button from "../../../../shared-components/css-modules/toyota/atoms/Button";
import Icon from "../../../../shared-components/css-modules/toyota/atoms/Icon";
import Text from "../../../../shared-components/css-modules/toyota/atoms/Text";
import Container from "../../../../shared-components/css-modules/toyota/atoms/Container";
import styles from "./Specs.module.scss";
import baseConfigStyles from "../Carconfig.module.scss";
import { useSlider } from "../../../../../../common-deprecated/features/apheleia/hooks/useSlider";
import { CarConfigStep } from "../../../../../../common-deprecated/features/apheleia/utils/carConfigNavigationUtils";
import { useSpecs } from "../../../hooks/useSpecs";
import a11y from "../../../../shared-components/css-modules/toyota/utils/Accessibility.module.scss";
import { CarConfigLabel, useCarConfigLabel } from "../../../utils/labelUtils";
import { TAB_SPECS } from "../../../utils/dataTestIDs";

const Specs: React.FC = () => {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const anyItemRef = useRef<HTMLDivElement | null>(null);

    const isSearching = useCarConfigSelector(selectCarConfigSpecsSearchIsSearching);
    const specs = useSpecs();

    const [hideHeader, setHideHeader] = useState(false);

    const trackStep = useAphStepTracking();

    useEffect(() => {
        trackStep(CarConfigStep.Specs);

        // UseEffect only needs to run once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isSearching) setHideHeader(false);
    }, [isSearching]);

    const { navigate, prevButtonDisabled, nextButtonDisabled, isDragging, elementProps } = useSlider(
        wrapperRef,
        anyItemRef,
    );

    const navigationLabel = useCarConfigLabel("carConfigA11yNavSpecs");

    return (
        <div id={TAB_SPECS}>
            <main className={styles.container} data-testid="specs-step">
                <header>
                    <Container>
                        <Text tag="h1" variant="apheleia-h1" spacing="spacing-6">
                            <CarConfigLabel label="carConfigSpecsTitle" />
                        </Text>
                    </Container>
                    <Search />
                </header>
                <Container>
                    <nav
                        className={clsx(styles.navigation, isSearching && styles["navigation-closed"])}
                        aria-label={navigationLabel}
                    >
                        <Button variant="btn-arrow" circle onClick={navigate(-1)} disabled={prevButtonDisabled}>
                            <Icon variant="chevron-left" sizing="md" aria-hidden="true" />
                            <span className={a11y["sr-only"]}>
                                <CarConfigLabel label="carConfigA11yButtonSlidePrevious" />
                            </span>
                        </Button>
                        <Button variant="btn-arrow" circle onClick={navigate(1)} disabled={nextButtonDisabled}>
                            <Icon variant="chevron-right" sizing="md" aria-hidden="true" />
                            <span className={a11y["sr-only"]}>
                                <CarConfigLabel label="carConfigA11yButtonSlideNext" />
                            </span>
                        </Button>
                    </nav>
                </Container>
                <section
                    onAnimationEnd={() => isSearching && setHideHeader(isSearching)}
                    className={`${styles.wrapper} ${
                        hideHeader ? styles.hidden : isSearching ? styles.closed : styles.opened
                    } `}
                >
                    <div className={styles["slider-wrapper"]}>
                        <div
                            className={clsx(styles.slider, isDragging && styles.dragging)}
                            ref={wrapperRef}
                            {...elementProps}
                        >
                            {specs.map((item, index) => (
                                <SpecGroup
                                    key={item.name}
                                    group={item}
                                    index={index}
                                    ref={index === 0 ? anyItemRef : undefined}
                                />
                            ))}
                        </div>
                    </div>
                </section>
                <section className={styles.ctas}>
                    <div className={baseConfigStyles.cta}>
                        <Cta />
                    </div>
                </section>
            </main>
        </div>
    );
};

export default Specs;
